/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-width is an internal custom property from #utils/skeleton.css, not a design token */
import type { PropertyValues } from 'lit'

import { property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { styleMap } from 'lit/directives/style-map.js'
import { repeat } from 'lit/directives/repeat.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { live } from 'lit/directives/live.js'
import { ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'

import { ariaElements, delegateEvents, refCollection } from '#utils/directives'
import LfdsIcon from '#components/icon/icon/lfds-icon.ts'
import { LfuiElement } from '#utils/element'
import {
	WithInternals,
	WithFocus,
	internals,
	compose,
	anchor
} from '#utils/mixins'

import LfdsSelectOption, { OPTION_CHANGE_EVENT } from './lfds-select-option.ts'
import LfdsSelectOptgroup from './lfds-select-optgroup.ts'
import skeletonStyles from '#utils/skeleton.css'
import { randomSkeletonPercentWidth, skeletonSrLabel } from '#utils/skeleton'

import styles from './lfds-select.css.ts'

/** Everything `lfds-select` accepts as a child of the rendered `<select>`. */
type SelectChild = LfdsSelectOption | LfdsSelectOptgroup | HTMLHRElement

const DELEGATED_EVENTS = [
	'change',
	'focus',
	'blur',
	'focusin',
	'focusout',
	'keydown',
	'keyup',
	'keypress'
]

/** Options already warned about — warn once per element, not once per render. */
const warnedOptions = new WeakSet<Element>()

function warnAboutOptionContent(option: LfdsSelectOption) {
	if (process.env.NODE_ENV === 'production') return
	if (!option.firstElementChild) return
	if (warnedOptions.has(option)) return

	warnedOptions.add(option)

	console.warn(
		'[lfds] ⚠ <lfds-select-option> content is text only — element children are ignored. ' +
			'Markup inside an option does not render, and the accessibility tree flattens an option ' +
			'to a single string in every browser, so it can never convey structure. Put the text in ' +
			'the `label` attribute, or use ' +
			'<lfds-list-row> (selectable) or <lfds-r-combobox> when the choices ' +
			'need rich content.',
		option
	)
}

/**
 * @name LfdsSelect
 * @tag lfds-select
 * @status beta
 *
 * @description
 * Native `<select>` with the LFDS label, help text and validation chrome
 * around it. Use `lfds-select-option` and `lfds-select-optgroup` as child
 * elements.
 *
 * @slot default - `lfds-select-option` and `lfds-select-optgroup` elements
 * @slot label - The visible label of the field
 */
export default class LfdsSelect extends compose(
	LfuiElement,
	WithInternals<HTMLSelectElement>,
	WithFocus
) {
	static styles = [LfuiElement.styles, skeletonStyles, styles]

	static events = ['change']

	@state() _value: string = ''
	/**
	 * Whether the `label` slot currently has content. Driven by `slotchange`, so
	 * it also follows a label added, replaced or removed after the first render —
	 * a light-DOM query at render time is only ever a snapshot.
	 */
	@state() _hasLabel: boolean = false
	@state() options: SelectChild[] = []

	/** Forwarded to the internal `<select autocomplete>` attribute. */
	@property() autocomplete: 'on' | 'off'

	/** Form field name, used as the key when the owning form is submitted. */
	@property() name: string

	/** Supporting text below the label. Part of the field's accessible description. */
	@property({ attribute: 'help-text' }) helpText: string

	/** Error text shown below the field. Set it together with `invalid`. */
	@property({ attribute: 'validation-message' }) validationMessage: string

	/** Number of visible rows, same as the native `<select size>` attribute. */
	@property({ type: Number }) size: number

	/** Announced as required and blocks form submission while nothing is selected. */
	@property({ type: Boolean }) required: boolean

	/** Disables the control and applies the disabled visual treatment. */
	@property({ type: Boolean }) disabled: boolean

	/**
	 * Shimmers the field's value and chevron in place instead of the real
	 * control, for content whose value isn't available yet.
	 */
	@property({ type: Boolean, reflect: true }) loading: boolean

	/** Focuses the field on first render. */
	@property({ type: Boolean }) autofocus: boolean = false

	/**
	 * Marks the field as invalid. Set `validation-message` together with it: the
	 * message is shown to the user and passed to the internal select's
	 * `setCustomValidity`.
	 */
	@property({ type: Boolean }) invalid: boolean

	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string
	@property({ attribute: 'sr-controls' }) srControls: string

	#skeletonWidth = randomSkeletonPercentWidth([45, 60, 70, 80])

	#labels = refCollection()
	#descriptions = refCollection()

	constructor() {
		super()
		LfdsIcon.preload('chevron-down', 24)
		LfdsIcon.preload('alert', 24)
	}

	connectedCallback() {
		super.connectedCallback()

		// Prevent the host (custom element) from being exposed as a separate node
		// in the accessibility tree. The inner native <select> carries the name,
		// role and value.
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')

		// Handle calling reportValidity on the parent form
		this.addEventListener('invalid', this.#handleInvalid)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('invalid', this.#handleInvalid)
	}

	#handleInvalid = () => {
		this.invalid = true
	}

	/**
	 * The native <select> lives in the shadow root, so the form reset algorithm
	 * never touches it: restoring the declared `selected` attributes and
	 * re-rendering is what lets the `live()` bindings put the native options back.
	 */
	async formResetCallback() {
		this.invalid = false

		for (const option of this.#flatOptions()) {
			option.selected = option.hasAttribute('selected')
		}

		this.requestUpdate()
		await this.updateComplete

		const select = this[anchor].value
		if (!select) return

		// Whatever the select ended up showing, which is not necessarily ''.
		this.#syncValueFromSelect(select)
	}

	/** Every option element in render order, flattened across optgroups. */
	#flatOptions(): LfdsSelectOption[] {
		const options: LfdsSelectOption[] = []

		for (const item of this.options) {
			if (item instanceof LfdsSelectOption) {
				options.push(item)
			} else if (item instanceof LfdsSelectOptgroup) {
				for (const child of item.options) {
					if (child instanceof LfdsSelectOption) options.push(child)
				}
			}
		}

		return options
	}

	/**
	 * Mirror the native selection back onto the light-DOM option elements.
	 * They are the source of truth for the `live()` bindings in #renderOption,
	 * so without this an unrelated re-render would revert the user's choice.
	 */
	#syncSelection(select: HTMLSelectElement) {
		const rendered = select.options

		this.#flatOptions().forEach((option, index) => {
			const target = rendered[index]
			if (target) option.selected = target.selected
		})
	}

	focus() {
		this[anchor].value?.focus()
	}

	get value() {
		return this._value
	}

	updated(changed: PropertyValues<this>) {
		// Anchors the browser's validation bubble to the native <select>.
		super.updated(changed)

		if (changed.has('loading')) {
			this.setAttribute('aria-busy', String(!!this.loading))
		}

		const select = this[anchor].value

		// The ref can be empty when an update is flushed after disconnect.
		if (!select) return

		// Read back after every render rather than on a particular property having
		// changed: an option can select itself at any time, with no property of this
		// element moving.
		if (select.value !== this._value) {
			this.#syncValueFromSelect(select)
		}

		const valueChanged = changed.has('_value') || changed.has('options')

		// The `this.name` guard keeps an entry keyed "undefined" out of the form.
		if (this.name && (valueChanged || changed.has('name'))) {
			this.#setFormValue(this._value)
		}
	}

	/** The one place `_value` is written. */
	#syncValueFromSelect(select: HTMLSelectElement) {
		this._value = select.value
	}

	#setFormValue(value: string) {
		const data = new FormData()
		data.set(this.name, value)
		this[internals].setFormValue(data)
	}

	#commitSelection(select: HTMLSelectElement) {
		this.#syncValueFromSelect(select)
		this.invalid = false
		this.#syncSelection(select)
	}

	#onchange = (event: Event) => {
		this.#commitSelection(event.target as HTMLSelectElement)
	}

	#onOptionChange = () => {
		this.requestUpdate()
	}

	#observed: Set<Element> = new Set()

	/**
	 * Listen for option changes on the current set of children only — an option
	 * moved out of this select must stop driving re-renders of it.
	 */
	#observe(elements: Iterable<Element>) {
		for (const previous of this.#observed) {
			previous.removeEventListener(OPTION_CHANGE_EVENT, this.#onOptionChange)
		}

		this.#observed = new Set(elements)

		for (const element of this.#observed) {
			element.addEventListener(OPTION_CHANGE_EVENT, this.#onOptionChange)
		}
	}

	#onslotchange = (event) => {
		this.options = event.target
			.assignedElements()
			.filter(
				(element) =>
					element instanceof LfdsSelectOption ||
					element instanceof LfdsSelectOptgroup ||
					element instanceof HTMLHRElement
			)

		this.#observe(this.options)
	}

	/** Single source of truth for whether the field has a visible label. */
	#onlabelchange = (event) => {
		this._hasLabel = event.target.assignedElements().length > 0
	}

	/**
	 * Option content is text only: the `label` property when set, otherwise the
	 * option's own text. Author nodes are read, never cloned — the accessibility
	 * tree flattens an option to a string anyway.
	 */
	#optionText(option: LfdsSelectOption): string {
		if (option.label) return option.label
		return option.textContent?.trim() ?? ''
	}

	#renderOption(option: LfdsSelectOption) {
		warnAboutOptionContent(option)

		// Never name an <option> with aria-label/aria-labelledby: a name on the
		// selected option replaces the value the field exposes, so the control
		// announces that name instead of what is shown in the trigger.
		return html`
			<option
				class="option"
				value=${ifDefined(option.value)}
				.selected=${live(option.selected)}
				?disabled=${option.disabled}
			>
				${this.#optionText(option)}
			</option>
		`
	}

	#renderItem = (item: SelectChild) => {
		if (item instanceof HTMLHRElement) return html`<hr class="divider" />`
		if (item instanceof LfdsSelectOption) return this.#renderOption(item)
		if (item instanceof LfdsSelectOptgroup) return this.#renderOptgroup(item)
		return nothing
	}

	#renderOptgroup(group: LfdsSelectOptgroup) {
		return html`
			<optgroup
				class="optgroup"
				label=${ifDefined(group.label)}
				?disabled=${group.disabled}
			>
				${repeat(group.options, (item) => item, this.#renderItem)}
			</optgroup>
		`
	}

	#renderItems() {
		return repeat(this.options, (item) => item, this.#renderItem)
	}

	/**
	 * The <label> renders unconditionally so `slotchange` stays a reliable signal
	 * for a label that appears or goes away later; with neither label nor help
	 * text the header is collapsed in CSS rather than removed from the tree.
	 *
	 * It joins `ariaLabelledByElements` only while it has content: an empty entry
	 * there can leave a field named by `sr-label` unnamed.
	 */
	#renderHeader() {
		return html`
			<div
				class=${classMap({
					header: true,
					empty: !this._hasLabel && !this.helpText
				})}
			>
				<div class="header-text">
					<label
						class="label"
						id="label"
						for="select"
						${this._hasLabel ? this.#labels.add() : nothing}
					>
						<slot name="label" @slotchange=${this.#onlabelchange}></slot>
					</label>

					${
						this.helpText
							? html`
									<span class="help description" ${this.#descriptions.add()}>
										${this.helpText}
									</span>
								`
							: nothing
					}
				</div>
			</div>
		`
	}

	#renderField() {
		return html`
			<div
				class=${classMap({
					box: true,
					invalid: this.invalid,
					loading: this.loading,
					// Empty = landed on a row with no value, i.e. the placeholder.
					empty: !this._value,
					['focus-visible']: this.focusVisible
				})}
			>
				<select
					id="select"
					class="select"
					style=${styleMap({
						'--lfds-skeleton-width': `${this.#skeletonWidth}%`
					})}
					aria-invalid=${ifDefined(this.invalid ? 'true' : undefined)}
					aria-label=${ifDefined(this.srLabel ?? undefined)}
					autocomplete=${ifDefined(this.autocomplete)}
					size=${ifDefined(this.size)}
					name=${ifDefined(this.name)}
					?autofocus=${this.autofocus}
					?disabled=${this.disabled || this.loading}
					?required=${this.required}
					${ref(this[anchor])}
					${this.#labels.bind('ariaLabelledByElements')}
					${this.#descriptions.bind('ariaDescribedByElements')}
					${delegateEvents(DELEGATED_EVENTS, {
						before: { change: this.#onchange }
					})}
					${ariaElements({
						describedBy: this.srDescribedby,
						labelledBy: this.srLabelledby,
						controls: this.srControls
					})}
				>
					${this.#renderItems()}
				</select>
				<lfds-icon icon-id="chevron-down" size="24" class="chevron"></lfds-icon>
			</div>
		`
	}

	/**
	 * The error joins the description, never the name: a control named "Frukt Du
	 * måste välja en frukt" is unusable, and the name is what a voice-control user
	 * has to speak. `role="alert"` announces it; the description keeps it
	 * reachable on every later visit.
	 */
	#renderValidation() {
		if (!this.validationMessage) return nothing

		return html`
			<div aria-atomic="true" role="alert" class="validation">
				<span class="icon">
					<lfds-icon icon-id="alert" size="24" color="currentcolor"></lfds-icon>
				</span>
				<span ${this.#descriptions.add()}> ${this.validationMessage} </span>
			</div>
		`
	}

	#renderLoadingDescription() {
		if (!this.loading) return nothing

		return html`<span class="hidden" role="status" ${this.#descriptions.add()}>
			${skeletonSrLabel(this)}
		</span>`
	}

	render() {
		return html`
			${this.#renderHeader()} ${this.#renderField()} ${this.#renderValidation()}
			${this.#renderLoadingDescription()}
			<slot @slotchange=${this.#onslotchange}></slot>
		`
	}
}
